import { createTheme } from '@mui/material/styles';

/** Тема UI Cascade / SMS (Montserrat + Inter). */
export const smsByTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FFB300',
      light: '#ffc233',
      dark: '#e6a100',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#6d28d9',
      contrastText: '#fff',
    },
    background: {
      default: '#fcfcfc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0d0d0d',
      /** На серых карточках (#f0f0f0) светлый slate (#94a3b8) почти нечитаем — темнее для WCAG */
      secondary: '#56606c',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#303030',
          color: '#fff',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 1px 0px rgba(0,0,0,0.14)',
          borderRadius: 8,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 72,
          '& .MuiTabs-indicator': {
            height: 2,
            backgroundColor: '#FFB300',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 72,
          textTransform: 'uppercase',
          fontSize: 14,
          fontWeight: 600,
          color: '#56606c',
          '&.Mui-selected': {
            color: '#FFB300',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '44px',
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
          gap: '10px',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.2,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 0 },
          '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
        },
        containedPrimary: {
          backgroundColor: '#FFB300',
          color: '#ffffff',
          border: '1px solid #FFB300',
          '&:hover': {
            backgroundColor: '#e6a200',
            borderColor: '#e6a200',
            color: '#ffffff',
          },
        },
        containedSecondary: {
          backgroundColor: '#8b5cf6',
          color: '#fff',
          border: '1px solid #8b5cf6',
          '&:hover': {
            backgroundColor: '#7c3aed',
            borderColor: '#7c3aed',
          },
        },
        outlined: {
          borderWidth: 1,
          borderColor: '#FFB300',
          color: 'rgba(0, 0, 0, 0.87)',
          '&:hover': {
            borderColor: '#e6a200',
            backgroundColor: 'rgba(255, 179, 0, 0.12)',
          },
        },
        text: {
          color: 'rgba(0, 0, 0, 0.87)',
          minHeight: 37,
          '&:hover': {
            backgroundColor: 'rgba(255, 179, 0, 0.14)',
          },
        },
        sizeSmall: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        sizeMedium: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '44px',
          padding: 8,
          '&:hover': {
            backgroundColor: 'rgba(255, 179, 0, 0.16)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
        },
        input: {
          '&::placeholder': {
            color: '#6b7480',
            opacity: 1,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: '#56606c',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#FFB300',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        },
      },
    },
  },
});

export const dataEdgeTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFB300',
      light: '#ffc233',
      dark: '#e6a200',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#6d28d9',
      contrastText: '#ffffff',
    },
    background: {
      default: '#171717',
      paper: '#252525',
    },
    text: {
      primary: '#f5f5f5',
      secondary: '#b8c0ca',
    },
    divider: 'rgba(255,255,255,0.1)',
  },
  typography: {
    fontFamily: '"Montserrat", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#252839',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 179, 0, 0.18)',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#252525',
          backgroundImage: 'none',
          borderRadius: 8,
          boxShadow: '0px 1px 1px 0px rgba(0,0,0,0.55)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 72,
          '& .MuiTabs-indicator': {
            height: 2,
            backgroundColor: '#FFB300',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 72,
          textTransform: 'uppercase',
          fontSize: 14,
          fontWeight: 600,
          color: '#b8c0ca',
          '&.Mui-selected': {
            color: '#FFB300',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '44px',
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
          gap: '10px',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.2,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 0 },
          '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
        },
        containedPrimary: {
          backgroundColor: '#FFB300',
          color: '#ffffff',
          border: '1px solid #FFB300',
          '&:hover': {
            backgroundColor: '#e6a200',
            borderColor: '#e6a200',
            color: '#ffffff',
          },
        },
        containedSecondary: {
          backgroundColor: '#8b5cf6',
          color: '#ffffff',
          border: '1px solid #8b5cf6',
          '&:hover': {
            backgroundColor: '#7c3aed',
            borderColor: '#7c3aed',
          },
        },
        outlined: {
          borderWidth: 1,
          borderColor: '#FFB300',
          color: '#f5f5f5',
          '&:hover': {
            borderColor: '#e6a200',
            backgroundColor: 'rgba(255, 179, 0, 0.12)',
          },
        },
        text: {
          color: '#f5f5f5',
          minHeight: 37,
          '&:hover': {
            backgroundColor: 'rgba(255, 179, 0, 0.14)',
          },
        },
        sizeSmall: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        sizeMedium: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '44px',
          padding: 8,
          '&:hover': {
            backgroundColor: 'rgba(255, 179, 0, 0.16)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
        },
        input: {
          '&::placeholder': {
            color: '#9aa3af',
            opacity: 1,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: '#b8c0ca',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#FFB300',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
        },
      },
    },
  },
});

export type ThemeId = 'smsby' | 'dataedge';

export const getTheme = (id: ThemeId) => (id === 'smsby' ? smsByTheme : dataEdgeTheme);
